/**
 * 날짜 파싱 유틸리티
 * 다양한 형식의 날짜 입력을 YYYY-MM-DD 형식으로 변환
 */

export interface DateParseResult {
  isValid: boolean
  date: Date | null
  formattedValue: string          // YYYY-MM-DD 형식
  originalInput: string
  errorMessage?: string
}

/**
 * 다양한 형식의 날짜 문자열을 파싱
 * 지원 형식:
 * - YYYY-MM-DD (2025-01-03)
 * - YYYY.MM.DD (2025.01.03)
 * - YYYY. MM. DD (2025. 01. 03)
 * - YYYY/MM/DD (2025/01/03)
 * - MM/DD/YYYY (01/03/2025)
 * - YYYY년 MM월 DD일 (2025년 1월 3일)
 */
export const parseDateInput = (input: string): DateParseResult => {
  const cleanInput = input.trim()

  if (!cleanInput) {
    return {
      isValid: false,
      date: null,
      formattedValue: '',
      originalInput: input,
      errorMessage: '날짜를 입력하세요'
    }
  }

  // 1. 기본 ISO 형식 (YYYY-MM-DD)
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(cleanInput)) {
    return parseISODate(cleanInput)
  }

  // 2. 점 구분 형식 (YYYY.MM.DD, YYYY. MM. DD)
  if (/^\d{4}\.?\s*\d{1,2}\.?\s*\d{1,2}$/.test(cleanInput)) {
    return parseDotSeparatedDate(cleanInput)
  }

  // 3. 슬래시 형식 (YYYY/MM/DD, MM/DD/YYYY)
  if (/^\d{1,4}\/\d{1,2}\/\d{2,4}$/.test(cleanInput)) {
    return parseSlashSeparatedDate(cleanInput)
  }

  // 4. 한국어 형식 (YYYY년 MM월 DD일)
  if (/^\d{4}년\s*\d{1,2}월\s*\d{1,2}일$/.test(cleanInput)) {
    return parseKoreanDate(cleanInput)
  }

  return {
    isValid: false,
    date: null,
    formattedValue: '',
    originalInput: input,
    errorMessage: '지원하지 않는 날짜 형식입니다. YYYY-MM-DD 또는 YYYY.MM.DD 형식으로 입력하세요.'
  }
}

/**
 * ISO 형식 날짜 파싱 (YYYY-MM-DD)
 */
const parseISODate = (input: string): DateParseResult => {
  const parts = input.split('-')
  if (parts.length !== 3) {
    return createInvalidResult(input, '올바른 날짜 형식이 아닙니다')
  }

  const year = parseInt(parts[0], 10)
  const month = parseInt(parts[1], 10)
  const day = parseInt(parts[2], 10)

  return validateAndCreateResult(year, month, day, input)
}

/**
 * 점 구분 형식 날짜 파싱 (YYYY.MM.DD, YYYY. MM. DD)
 */
const parseDotSeparatedDate = (input: string): DateParseResult => {
  // 공백 제거하고 점을 하이픈으로 변경
  const cleaned = input.replace(/\s/g, '').replace(/\./g, '-')

  // 마지막 문자가 하이픈인 경우 제거 (YYYY. MM. DD. 형태 처리)
  const normalizedInput = cleaned.replace(/-$/, '')

  return parseISODate(normalizedInput)
}

/**
 * 슬래시 형식 날짜 파싱 (YYYY/MM/DD, MM/DD/YYYY)
 */
const parseSlashSeparatedDate = (input: string): DateParseResult => {
  const parts = input.split('/')
  if (parts.length !== 3) {
    return createInvalidResult(input, '올바른 날짜 형식이 아닙니다')
  }

  const num1 = parseInt(parts[0], 10)
  const num2 = parseInt(parts[1], 10)
  const num3 = parseInt(parts[2], 10)

  // YYYY/MM/DD 형식 (첫 번째 숫자가 4자리)
  if (parts[0].length === 4) {
    return validateAndCreateResult(num1, num2, num3, input)
  }

  // MM/DD/YYYY 형식 (마지막 숫자가 4자리)
  if (parts[2].length === 4) {
    return validateAndCreateResult(num3, num1, num2, input)
  }

  return createInvalidResult(input, '연도는 4자리로 입력하세요')
}

/**
 * 한국어 형식 날짜 파싱 (YYYY년 MM월 DD일)
 */
const parseKoreanDate = (input: string): DateParseResult => {
  const match = input.match(/^(\d{4})년\s*(\d{1,2})월\s*(\d{1,2})일$/)

  if (!match) {
    return createInvalidResult(input, '올바른 한국어 날짜 형식이 아닙니다')
  }

  const year = parseInt(match[1], 10)
  const month = parseInt(match[2], 10)
  const day = parseInt(match[3], 10)

  return validateAndCreateResult(year, month, day, input)
}

/**
 * 날짜 유효성 검증 및 결과 생성
 */
const validateAndCreateResult = (year: number, month: number, day: number, originalInput: string): DateParseResult => {
  // 기본 범위 검증
  if (year < 1900 || year > 2100) {
    return createInvalidResult(originalInput, '연도는 1900년에서 2100년 사이여야 합니다')
  }

  if (month < 1 || month > 12) {
    return createInvalidResult(originalInput, '월은 1에서 12 사이여야 합니다')
  }

  if (day < 1 || day > 31) {
    return createInvalidResult(originalInput, '일은 1에서 31 사이여야 합니다')
  }

  // Date 객체로 정확한 날짜 검증
  const date = new Date(year, month - 1, day)

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return createInvalidResult(originalInput, '존재하지 않는 날짜입니다')
  }

  // YYYY-MM-DD 형식으로 포맷팅
  const formattedValue = `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`

  return {
    isValid: true,
    date,
    formattedValue,
    originalInput
  }
}

/**
 * 유효하지 않은 결과 생성 헬퍼
 */
const createInvalidResult = (originalInput: string, errorMessage: string): DateParseResult => {
  return {
    isValid: false,
    date: null,
    formattedValue: '',
    originalInput,
    errorMessage
  }
}

/**
 * 날짜를 사용자가 입력하기 편한 형식으로 변환
 */
export const formatDateForEditing = (isoDate: string): string => {
  if (!isoDate) return ''

  // 이미 올바른 형식이면 그대로 반환
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    return isoDate
  }

  // 다른 형식이면 파싱해서 표준 형식으로 변환
  const parseResult = parseDateInput(isoDate)
  return parseResult.isValid ? parseResult.formattedValue : isoDate
}

/**
 * 날짜를 화면 표시용 형식으로 변환
 */
export const formatDateForDisplay = (isoDate: string): string => {
  if (!isoDate) return ''

  const parseResult = parseDateInput(isoDate)
  if (!parseResult.isValid || !parseResult.date) return isoDate

  // 한국어 형식으로 표시
  const year = parseResult.date.getFullYear()
  const month = parseResult.date.getMonth() + 1
  const day = parseResult.date.getDate()

  return `${year}년 ${month}월 ${day}일`
}

/**
 * ISO 날짜/시간 문자열을 한국어 형식으로 변환
 * @param isoDateTime - ISO 8601 형식의 날짜/시간 문자열
 * @returns 포맷팅된 날짜/시간 문자열 (예: "2025-10-15 14:30")
 */
export const formatDateTime = (isoDateTime: string): string => {
  if (!isoDateTime) return ''

  try {
    const date = new Date(isoDateTime)

    // 유효한 날짜인지 확인
    if (isNaN(date.getTime())) {
      return isoDateTime
    }

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')

    return `${year}-${month}-${day} ${hours}:${minutes}`
  } catch (error) {
    return isoDateTime
  }
}

/**
 * 이번 주의 월요일과 일요일 날짜를 반환
 * @returns Object with start (Monday) and end (Sunday) dates in 'YYYY-MM-DD' format
 */
export const getThisWeekRange = (): { start: string; end: string } => {
  const today = new Date()
  const dayOfWeek = today.getDay() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

  // Calculate Monday (start of week)
  const monday = new Date(today)
  const daysFromMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
  monday.setDate(today.getDate() + daysFromMonday)

  // Calculate Sunday (end of week)
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)

  // Format to 'YYYY-MM-DD'
  const formatDate = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  return {
    start: formatDate(monday),
    end: formatDate(sunday)
  }
}